/**
 * pm-cro-pdp.js - behaviour for the CRO product template
 * (sections/pm-cro-product.liquid, templates/product.cro.json).
 *
 * It REPLACES pm-pdp.js on that template. Never load both: they would bind
 * duplicate sticky-bar and size-guide handlers.
 *
 * 1. BUY BUTTON MIRROR
 *    The first available variant is pre-selected (merchant decision), so the
 *    page needs no size gate. The mobile sticky bar mirrors the real button
 *    and re-syncs on every PUB_SUB_EVENTS.variantChange, i.e. after Dawn has
 *    finished re-rendering for the new size. Its price is copied from the
 *    re-rendered section HTML.
 *
 * 2. MOBILE STICKY BAR
 *    Shown once the real button has scrolled out of view; tapping it clicks
 *    the real button, so there is one checkout path and one variant id.
 *
 * 3. SIZE GUIDE
 *    Delegated document-level handlers, so the guide keeps working after Dawn
 *    replaces <variant-selects> (pm-pdp.js bound directly, once).
 *
 * 4. SALE COUNTDOWN (<pm-cro-countdown>)
 *    "live": counts down to the real end date from the section setting and
 *    hides itself at zero. "demo" (setting cro_demo_urgency, merchant request):
 *    simulated, ends at 23:59 in the visitor's local time and restarts for the
 *    next day. "sample": theme editor only, a 24 hour preview.
 *
 * 5. ORDER TIMER + DELIVERY WINDOW ([data-pm-cro-delivery])
 *    Order timer (merchant request): 3 hours per browser session, stored in
 *    sessionStorage so it survives reloads and page changes within the
 *    session; a new session starts a new 3 hours, and an expired window
 *    restarts. The arrival window underneath is the real one: processing +
 *    transit business days from the Shipping policy, counted from the next
 *    business-day cutoff in the store's timezone, weekends and listed
 *    holidays skipped.
 */
(function () {
  'use strict';

  var LABEL_ADD = 'Add to bag';

  /* ------------------------------------------------------------------ */
  /* helpers                                                            */
  /* ------------------------------------------------------------------ */
  function root() {
    return document.querySelector('product-info[data-pm-cro]');
  }
  function submitButton(r) {
    return r ? r.querySelector('.product-form__submit') : null;
  }
  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /* ------------------------------------------------------------------ */
  /* 1. buy button mirror                                               */
  /* ------------------------------------------------------------------ */
  function syncSticky(r) {
    var btn = submitButton(r);
    var sticky = document.querySelector('[data-pm-sticky-btn]');
    if (!btn || !sticky) return;
    var span = btn.querySelector('span');
    var unavailable = btn.hasAttribute('disabled');
    sticky.disabled = unavailable;
    sticky.textContent = unavailable && span ? (span.textContent || '').trim() : LABEL_ADD;
  }

  function updateStickyPrice(html) {
    var dest = document.querySelector('[data-pm-sticky-price]');
    var src = html && html.querySelector ? html.querySelector('[data-pm-sticky-price]') : null;
    if (dest && src) dest.innerHTML = src.innerHTML;
  }

  function initButtonMirror() {
    var r = root();
    if (!r || r.dataset.pmCroMirror) return;
    r.dataset.pmCroMirror = '1';
    syncSticky(r);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var cur = root();
        if (!cur) return;
        var data = event && event.data;
        if (data && data.sectionId && data.sectionId !== cur.dataset.section) return;
        syncSticky(cur);
        if (data && data.html) updateStickyPrice(data.html);
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* 2. mobile sticky bar                                               */
  /* ------------------------------------------------------------------ */
  function initStickyBar() {
    var bar = document.querySelector('[data-pm-sticky-bar]');
    var r = root();
    var target = submitButton(r);
    if (!bar || !r || !target || bar.dataset.pmCroReady) return;
    bar.dataset.pmCroReady = '1';

    // Must match the CSS breakpoint that hides the bar (min-width: 990px).
    var isDesktop = window.matchMedia('(min-width: 990px)');
    function setVisible(on) {
      if (on && isDesktop.matches) on = false;
      bar.dataset.visible = on ? 'true' : 'false';
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      document.body.classList.toggle('pm-sticky-active', on);
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          var e = entries[0];
          setVisible(!e.isIntersecting && e.boundingClientRect.top < 0);
        },
        { threshold: 0 }
      ).observe(target);
    }

    var stickyBtn = bar.querySelector('[data-pm-sticky-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        var real = submitButton(root());
        // Disabled = unavailable variant, or Dawn mid re-render for a new size.
        if (!real || real.hasAttribute('disabled')) return;
        real.click();
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* 3. size guide (delegated)                                          */
  /* ------------------------------------------------------------------ */
  function initSizeGuide() {
    if (document.documentElement.dataset.pmCroGuide) return;
    document.documentElement.dataset.pmCroGuide = '1';

    document.addEventListener('click', function (event) {
      var el = event.target;
      if (!el || !el.closest) return;

      var trigger = el.closest('[data-pm-size-guide-trigger]');
      if (trigger) {
        var guide = trigger.closest('[data-pm-size-guide]');
        var dlg = guide && guide.querySelector('[data-pm-size-guide-dialog]');
        if (!dlg || dlg.open) return;
        var source = dlg.querySelector('[data-pm-size-guide-source]');
        if (source && !source.querySelector('table')) {
          var note = dlg.querySelector('[data-pm-size-guide-empty]');
          if (note) note.hidden = false;
        }
        dlg._pmReturnFocus = trigger;
        dlg.showModal();
        document.body.classList.add('pm-size-guide-open');
        return;
      }

      var closeBtn = el.closest('[data-pm-size-guide-close]');
      if (closeBtn) {
        var d = closeBtn.closest('dialog');
        if (d && d.open) d.close();
        return;
      }

      // Click on the backdrop: the event target is the dialog element itself.
      if (el.matches && el.matches('dialog[data-pm-size-guide-dialog]') && el.open) {
        el.close();
        return;
      }

      var unit = el.closest('[data-pm-unit]');
      if (unit) {
        var scope = unit.closest('[data-pm-size-guide]') || document;
        var value = unit.dataset.pmUnit;
        scope.querySelectorAll('[data-pm-unit]').forEach(function (b) {
          b.setAttribute('aria-pressed', b === unit ? 'true' : 'false');
        });
        scope.querySelectorAll('[data-unit]').forEach(function (x) {
          x.style.display = x.dataset.unit === value ? 'inline' : 'none';
        });
      }
    });

    // 'close' does not bubble; a capture listener still sees it (Esc included).
    document.addEventListener(
      'close',
      function (event) {
        var d = event.target;
        if (!d || !d.matches || !d.matches('dialog[data-pm-size-guide-dialog]')) return;
        document.body.classList.remove('pm-size-guide-open');
        if (d._pmReturnFocus && document.contains(d._pmReturnFocus)) d._pmReturnFocus.focus();
      },
      true
    );
  }

  /* ------------------------------------------------------------------ */
  /* 4. sale countdown                                                  */
  /* ------------------------------------------------------------------ */
  function initCountdowns() {
    document.querySelectorAll('pm-cro-countdown').forEach(function (el) {
      if (el.dataset.pmReady) return;
      el.dataset.pmReady = '1';

      var mode = el.dataset.mode;
      // Demo mode: today's 23:59 in the visitor's local time (tomorrow's once passed).
      function nextLocal2359(from) {
        var d = new Date(from);
        d.setHours(23, 59, 0, 0);
        if (d.getTime() <= from) d.setDate(d.getDate() + 1);
        return d.getTime();
      }
      var end;
      if (mode === 'sample') {
        end = Date.now() + 86399000;
      } else if (mode === 'demo') {
        end = nextLocal2359(Date.now());
      } else {
        end = Date.parse(el.dataset.endsAt || '');
        if (isNaN(end)) {
          el.hidden = true;
          return;
        }
      }

      var daysCell = el.querySelector('[data-cro-days-cell]');
      var lastLabel = 0;
      function set(sel, v) {
        var n = el.querySelector(sel);
        if (n) n.textContent = pad(v);
      }
      function tick() {
        var left = end - Date.now();
        if (left <= 0) {
          if (mode === 'live') {
            el.hidden = true; // a real sale is over: no restart
            return;
          }
          if (mode === 'demo') {
            end = nextLocal2359(Date.now() + 1000);
            left = end - Date.now();
          } else {
            left = 0;
          }
        }
        var s = Math.floor(left / 1000);
        var d = Math.floor(s / 86400);
        var h = Math.floor((s % 86400) / 3600);
        var m = Math.floor((s % 3600) / 60);
        set('[data-cro-days]', d);
        set('[data-cro-hours]', h);
        set('[data-cro-minutes]', m);
        set('[data-cro-seconds]', s % 60);
        if (daysCell) daysCell.hidden = d === 0;
        el.classList.remove('is-pending');
        if (Date.now() - lastLabel > 60000) {
          var label = (el.querySelector('strong') || {}).textContent || 'Sale';
          el.setAttribute('aria-label', label + ' ends in ' + d + ' days ' + h + ' hours ' + m + ' minutes');
          lastLabel = Date.now();
        }
        if (left > 0) setTimeout(tick, 1000 - (Date.now() % 1000));
      }
      tick();
    });
  }

  /* ------------------------------------------------------------------ */
  /* 5. order timer + delivery window                                   */
  /* ------------------------------------------------------------------ */
  /* PM-CRO-DELIVERY-CORE:BEGIN (pure functions, unit-tested in Node) */
  function croWallClock(ms, tz) {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date(ms));
    var o = {};
    parts.forEach(function (p) {
      if (p.type !== 'literal') o[p.type] = Number(p.value);
    });
    return { y: o.year, m: o.month, d: o.day, h: o.hour === 24 ? 0 : o.hour, mi: o.minute, s: o.second };
  }
  function croYmd(o) {
    return o.y + '-' + (o.m < 10 ? '0' : '') + o.m + '-' + (o.d < 10 ? '0' : '') + o.d;
  }
  function croAddDays(o, n) {
    var x = new Date(Date.UTC(o.y, o.m - 1, o.d));
    x.setUTCDate(x.getUTCDate() + n);
    return { y: x.getUTCFullYear(), m: x.getUTCMonth() + 1, d: x.getUTCDate() };
  }
  function croIsBiz(o, holidays) {
    var dow = new Date(Date.UTC(o.y, o.m - 1, o.d)).getUTCDay();
    return dow !== 0 && dow !== 6 && !holidays[croYmd(o)];
  }
  function croNextBiz(o, holidays) {
    var x = croAddDays(o, 1);
    while (!croIsBiz(x, holidays)) x = croAddDays(x, 1);
    return x;
  }
  function croAddBiz(o, n, holidays) {
    var x = o;
    var c = 0;
    while (c < n) {
      x = croAddDays(x, 1);
      if (croIsBiz(x, holidays)) c++;
    }
    return x;
  }
  // UTC milliseconds for a wall-clock time in tz (DST-safe: two passes).
  function croZonedToUtc(y, m, d, h, mi, tz) {
    var guess = Date.UTC(y, m - 1, d, h, mi, 0);
    for (var i = 0; i < 2; i++) {
      var w = croWallClock(guess, tz);
      var asUtc = Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi, w.s);
      guess += Date.UTC(y, m - 1, d, h, mi, 0) - asUtc;
    }
    return guess;
  }
  function croParseHolidays(str) {
    var set = {};
    String(str || '')
      .split(/[\s,]+/)
      .forEach(function (s) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) set[s] = true;
      });
    return set;
  }
  // cfg: tz, cutoffHour, cutoffMinute, procMin, procMax, transitMin, transitMax, holidays (set)
  function croDeliveryPlan(nowMs, cfg) {
    var w = croWallClock(nowMs, cfg.tz);
    var today = { y: w.y, m: w.m, d: w.d };
    var before = w.h < cfg.cutoffHour || (w.h === cfg.cutoffHour && w.mi < cfg.cutoffMinute);
    var start = croIsBiz(today, cfg.holidays) && before ? today : croNextBiz(today, cfg.holidays);
    var cutoffMs = croZonedToUtc(start.y, start.m, start.d, cfg.cutoffHour, cfg.cutoffMinute, cfg.tz);
    return {
      start: start,
      cutoffMs: cutoffMs,
      msLeft: cutoffMs - nowMs,
      earliest: croAddBiz(start, cfg.procMin + cfg.transitMin, cfg.holidays),
      latest: croAddBiz(start, cfg.procMax + cfg.transitMax, cfg.holidays)
    };
  }
  /* PM-CRO-DELIVERY-CORE:END */

  function fmtDay(o) {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
      new Date(Date.UTC(o.y, o.m - 1, o.d))
    );
  }
  // "2 hr 59 min", "45 min", "3 hr"; never "0 min" in the last minute.
  function fmtOrderWindow(ms) {
    var total = Math.max(1, Math.floor(ms / 60000));
    var h = Math.floor(total / 60);
    var m = total % 60;
    if (!h) return m + ' min';
    return m ? h + ' hr ' + m + ' min' : h + ' hr';
  }

  // Per-session order window (merchant request): N hours from the first page
  // view of this browser session, shared by every page in the session.
  var SESSION_KEY = 'pmCroOrderBy';
  var sessionOrderBy = 0;
  function sessionDeadline(now, spanMs) {
    if (sessionOrderBy > now) return sessionOrderBy;
    var stored = 0;
    try {
      stored = Number(window.sessionStorage.getItem(SESSION_KEY)) || 0;
    } catch (e) {
      /* storage blocked: keep the window in memory for this page */
    }
    sessionOrderBy = stored > now ? stored : now + spanMs;
    try {
      window.sessionStorage.setItem(SESSION_KEY, String(sessionOrderBy));
    } catch (e) {
      /* ignore */
    }
    return sessionOrderBy;
  }

  function initDelivery() {
    document.querySelectorAll('[data-pm-cro-delivery]').forEach(function (el) {
      if (el.dataset.pmReady) return;
      el.dataset.pmReady = '1';
      var out = el.querySelector('[data-pm-cro-delivery-text]');
      if (!out) return;
      var cfg = {
        tz: el.dataset.tz || 'America/New_York',
        cutoffHour: Number(el.dataset.cutoffHour || 14),
        cutoffMinute: Number(el.dataset.cutoffMinute || 0),
        procMin: Number(el.dataset.procMin || 0),
        procMax: Number(el.dataset.procMax || 2),
        transitMin: Number(el.dataset.transitMin || 5),
        transitMax: Number(el.dataset.transitMax || 12),
        holidays: croParseHolidays(el.dataset.holidays)
      };
      var spanMs = Math.max(1, Number(el.dataset.sessionHours || 3)) * 3600000;
      var plan = null;
      function render() {
        var now = Date.now();
        if (!plan || now >= plan.cutoffMs) plan = croDeliveryPlan(now, cfg);
        var range = fmtDay(plan.earliest) + ' - ' + fmtDay(plan.latest);
        var left = sessionDeadline(now, spanMs) - now;
        out.innerHTML =
          'Order within <b class="pm-cro-delivery__left">' + fmtOrderWindow(left) + '</b> for same-day processing' +
          '<span class="pm-cro-delivery__eta">Free shipping <span aria-hidden="true">·</span> Estimated delivery <b>' + range + '</b></span>';
        setTimeout(render, 1000 - (Date.now() % 1000));
      }
      try {
        render();
        el.classList.add('is-ready');
      } catch (e) {
        /* keep the static policy fallback */
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* boot                                                               */
  /* ------------------------------------------------------------------ */
  function init() {
    initButtonMirror();
    initStickyBar();
    initSizeGuide();
    initCountdowns();
    initDelivery();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
